export type AddressIdentityProvider = "google_places" | "google_geocoder";

/**
 * One normalized address identity shared by navigation and property lookup.
 *
 * A formatted address is useful display text, but it cannot safely stand in
 * for county, unit, coordinates, or a provider identifier. Every field is
 * present on the parsed contract so downstream code cannot confuse "not
 * selected" with "forgotten during navigation".
 */
export interface StructuredAddressIdentity {
  formattedAddress: string;
  street: string | null;
  unit: string | null;
  city: string | null;
  county: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  provider: AddressIdentityProvider | null;
  providerPlaceId: string | null;
}

/** Partial provider/geocoder input accepted while an address is being entered. */
export type AddressNavigationLocation = Partial<StructuredAddressIdentity>;

/** The existing ordinary-lookup ZIP gate; a token need not be at the end. */
export function hasPropertyLookupZip(address: string): boolean {
  return /\b(\d{5})(?:-\d{4})?\b/.test(address);
}

function finiteCoordinate(value: string | null): number | null {
  if (!value?.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Reads the same structured identity contract emitted by every address form. */
export function parseAddressNavigationLocation(
  params: Pick<URLSearchParams, "get">,
): StructuredAddressIdentity {
  const provider = params.get("address_provider");
  return {
    formattedAddress: params.get("address")?.trim() ?? "",
    street: params.get("street")?.trim() || null,
    unit: params.get("unit")?.trim() || null,
    city: params.get("city")?.trim() || null,
    county: params.get("county")?.trim() || null,
    state: params.get("state")?.trim().toUpperCase() || null,
    zip: params.get("zip")?.trim() || null,
    country: params.get("country")?.trim().toUpperCase() || null,
    latitude: finiteCoordinate(params.get("lat")),
    longitude: finiteCoordinate(params.get("lng")),
    provider:
      provider === "google_places" || provider === "google_geocoder"
        ? provider
        : null,
    providerPlaceId: params.get("place_id")?.trim() || null,
  };
}

/**
 * Returns a JSON-safe copy for the browser-to-Edge request boundary. The Edge
 * function may use these fields only as identity hints: it still owns county
 * resolution and assessment-source authority.
 */
export function buildPropertyLookupAddressIdentity(
  formattedAddress: string,
  location?: AddressNavigationLocation | null,
): StructuredAddressIdentity {
  return {
    formattedAddress: formattedAddress.trim(),
    street: location?.street?.trim() || null,
    unit: location?.unit?.trim() || null,
    city: location?.city?.trim() || null,
    county: location?.county?.trim() || null,
    state: location?.state?.trim().toUpperCase() || null,
    zip: location?.zip?.trim() || null,
    country: location?.country?.trim().toUpperCase() || null,
    latitude:
      typeof location?.latitude === "number" && Number.isFinite(location.latitude)
        ? location.latitude
        : null,
    longitude:
      typeof location?.longitude === "number" && Number.isFinite(location.longitude)
        ? location.longitude
        : null,
    provider: location?.provider ?? null,
    providerPlaceId: location?.providerPlaceId?.trim() || null,
  };
}

/**
 * Builds an address route without discarding structured jurisdiction data.
 * The formatted street address is not a reliable substitute for the county
 * component returned by the geocoder, especially where ZIP codes cross
 * county lines or a state routes assessment work below the county level.
 */
export function buildAddressNavigationPath(
  path: string,
  address: string,
  location?: AddressNavigationLocation | null,
): string {
  const params = new URLSearchParams({ address });
  const street = location?.street?.trim();
  const unit = location?.unit?.trim();
  const city = location?.city?.trim();
  const county = location?.county?.trim();
  const state = (location?.state?.trim() ?? "").toUpperCase();
  const zip = location?.zip?.trim();
  const country = (location?.country?.trim() ?? "").toUpperCase();
  const providerPlaceId = location?.providerPlaceId?.trim();

  if (street) params.set("street", street);
  if (unit) params.set("unit", unit);
  if (city) params.set("city", city);
  if (county) params.set("county", county);
  if (state) params.set("state", state);
  if (zip) params.set("zip", zip);
  if (country) params.set("country", country);
  if (typeof location?.latitude === "number" && Number.isFinite(location.latitude)) {
    params.set("lat", String(location.latitude));
  }
  if (typeof location?.longitude === "number" && Number.isFinite(location.longitude)) {
    params.set("lng", String(location.longitude));
  }
  if (location?.provider) params.set("address_provider", location.provider);
  if (providerPlaceId) params.set("place_id", providerPlaceId);

  return `${path}?${params.toString()}`;
}
